// Browser-side wrapper around `composeSyllabus` from @dosfilos/application.
// Loads the Tenant + Department + Program + Course + CourseTemplate entities
// from Firestore using the web SDK, reconstitutes domain objects, and returns
// the ComposedSyllabus (the single source of truth for the syllabus PDF
// renderer and the student portal).

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Course, CourseTemplate, Department, Program, Tenant, TenantId } from '@dosfilos/domain';
import { composeSyllabus, type ComposedSyllabus } from '@dosfilos/application/composition';

// ─── Helpers ────────────────────────────────────────────────────────────────

function toDate(v: unknown): Date {
  if (!v) return new Date();
  if (typeof v === 'object' && v !== null && 'toDate' in (v as object)) {
    return (v as { toDate: () => Date }).toDate();
  }
  if (typeof v === 'string') return new Date(v);
  if (v instanceof Date) return v;
  return new Date();
}

// ─── Loaders ────────────────────────────────────────────────────────────────

async function loadTenant(tenantId: string): Promise<Tenant | null> {
  const snap = await getDoc(doc(db, 'tenants', tenantId));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  const cfg = (data['config'] ?? {}) as Record<string, unknown>;
  // Backcompat: legacy single researchRubric → rubrics[0]
  if (!cfg['rubrics'] && cfg['researchRubric']) {
    cfg['rubrics'] = [cfg['researchRubric']];
  }
  return Tenant.reconstitute({
    id: TenantId.of(tenantId),
    name: (data['name'] as string) ?? '',
    slug: (data['slug'] as string) ?? '',
    status: (data['status'] as 'active' | 'suspended' | 'archived') ?? 'active',
    config: cfg as any,
    createdAt: toDate(data['createdAt']),
    updatedAt: toDate(data['updatedAt']),
  });
}

async function loadProgram(tenantId: string, programId: string): Promise<Program | null> {
  const snap = await getDoc(doc(db, `tenants/${tenantId}/programs/${programId}`));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  // Backcompat: legacy researchRubric → rubrics[0]
  if (!data['rubrics'] && data['researchRubric']) {
    data['rubrics'] = [data['researchRubric']];
  }
  return Program.reconstitute({
    id: programId,
    tenantId: TenantId.of(tenantId),
    name: (data['name'] as string) ?? '',
    code: (data['code'] as string) ?? '',
    durationTerms: (data['durationTerms'] as number) ?? 0,
    status: (data['status'] as any) ?? 'active',
    documentRequirements: (data['documentRequirements'] as any[]) ?? [],
    reviewerConfig: (data['reviewerConfig'] as any[]) ?? [],
    ...(data['description'] ? { description: data['description'] as string } : {}),
    ...(data['purpose'] ? { purpose: data['purpose'] as string } : {}),
    ...(data['departmentId'] ? { departmentId: data['departmentId'] as string } : {}),
    ...(data['learningObjectives']
      ? { learningObjectives: data['learningObjectives'] as any[] }
      : {}),
    ...(data['departmentName'] ? { departmentName: data['departmentName'] as string } : {}),
    ...(data['departmentPurpose']
      ? { departmentPurpose: data['departmentPurpose'] as string }
      : {}),
    ...(data['departmentObjectives']
      ? { departmentObjectives: data['departmentObjectives'] as any[] }
      : {}),
    ...(data['philosophy'] ? { philosophy: data['philosophy'] as any[] } : {}),
    ...(data['policies'] ? { policies: data['policies'] as any[] } : {}),
    ...(data['latePolicy'] ? { latePolicy: data['latePolicy'] as any } : {}),
    ...(data['modality'] ? { modality: data['modality'] as any } : {}),
    ...(data['technologyRequirements']
      ? { technologyRequirements: data['technologyRequirements'] as string }
      : {}),
    ...(data['researchRubric'] ? { researchRubric: data['researchRubric'] as any } : {}),
    ...(data['rubrics'] ? { rubrics: data['rubrics'] as any[] } : {}),
    ...(data['primaryColor'] ? { primaryColor: data['primaryColor'] as string } : {}),
    ...(data['formTemplateId'] ? { formTemplateId: data['formTemplateId'] as string } : {}),
    ...(data['feeConfig'] ? { feeConfig: data['feeConfig'] as any } : {}),
    createdAt: toDate(data['createdAt']),
    updatedAt: toDate(data['updatedAt']),
  });
}

async function loadDepartment(tenantId: string, departmentId: string): Promise<Department | null> {
  const snap = await getDoc(doc(db, `tenants/${tenantId}/departments/${departmentId}`));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return Department.reconstitute({
    id: departmentId,
    tenantId: TenantId.of(tenantId),
    name: (data['name'] as string) ?? '',
    code: (data['code'] as string) ?? '',
    status: ((data['status'] as 'active' | 'inactive' | undefined) ?? 'active') as
      | 'active'
      | 'inactive',
    ...(data['purpose'] ? { purpose: data['purpose'] as string } : {}),
    ...(data['learningObjectives']
      ? { learningObjectives: data['learningObjectives'] as any[] }
      : {}),
    ...(data['primaryColor'] ? { primaryColor: data['primaryColor'] as string } : {}),
    createdAt: toDate(data['createdAt']),
    updatedAt: toDate(data['updatedAt']),
  });
}

async function loadCourse(tenantId: string, courseId: string): Promise<Course | null> {
  const snap = await getDoc(doc(db, `tenants/${tenantId}/courses/${courseId}`));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return Course.reconstitute({
    id: courseId,
    tenantId: TenantId.of(tenantId),
    name: (data['name'] as string) ?? '',
    code: (data['code'] as string) ?? '',
    credits: (data['credits'] as number) ?? 0,
    prerequisites: (data['prerequisites'] as string[]) ?? [],
    status: ((data['status'] as any) ?? 'active') as any,
    ...(data['programId'] ? { programId: data['programId'] as string } : {}),
    ...(data['description'] ? { description: data['description'] as string } : {}),
    ...(data['learningObjectives']
      ? { learningObjectives: data['learningObjectives'] as any[] }
      : {}),
    ...(data['anchorVerse'] ? { anchorVerse: data['anchorVerse'] as any } : {}),
    ...(data['biblicalVersion'] ? { biblicalVersion: data['biblicalVersion'] as string } : {}),
    ...(data['recommendedReadings']
      ? { recommendedReadings: data['recommendedReadings'] as any[] }
      : {}),
    createdAt: toDate(data['createdAt']),
    updatedAt: toDate(data['updatedAt']),
  });
}

// ─── Public API ─────────────────────────────────────────────────────────────

export interface ComposeOptions {
  /** Tenant id (always required). */
  tenantId: string;
  /** Full CourseTemplate (already in memory — caller usually has the editor state). */
  template: CourseTemplate;
}

/**
 * Resolves the full inheritance chain (Tenant → Department → Program → Course
 * → Template) using Firestore reads and returns a `ComposedSyllabus` ready for
 * rendering. Missing layers are tolerated (composer falls back).
 */
export async function loadComposedSyllabus(options: ComposeOptions): Promise<ComposedSyllabus> {
  const { tenantId, template } = options;
  const tplSnap = template.toSnapshot();

  const [tenant, course, program] = await Promise.all([
    loadTenant(tenantId),
    tplSnap.courseId ? loadCourse(tenantId, tplSnap.courseId) : Promise.resolve(null),
    tplSnap.programId ? loadProgram(tenantId, tplSnap.programId) : Promise.resolve(null),
  ]);

  // No fallback to `course.programId`. It holds ONE program per course and is
  // `@deprecated`; falling back to it made the preview show a program the
  // student's own document may never use — the section decides that, and a
  // course can be cross-listed. With no program on the template, the preview
  // shows no program layer, which is the honest answer.
  const resolvedProgram = program;

  const departmentId = resolvedProgram?.toSnapshot().departmentId;
  const department = departmentId ? await loadDepartment(tenantId, departmentId) : null;

  return composeSyllabus(template, {
    tenant,
    program: resolvedProgram,
    department,
    course,
  });
}

/**
 * Compose ONLY the inherited layers (Tenant → Department → Program → Course)
 * for a given course/program link, using an empty template stub so the result
 * reflects what those entities contribute — not the template's own content.
 * The editor uses this to show inherited data read-only per section.
 */
export async function loadInheritance(
  tenantId: string,
  courseId?: string,
  programId?: string,
): Promise<ComposedSyllabus> {
  const stub = CourseTemplate.create({
    id: 'inheritance-preview',
    tenantId: TenantId.of(tenantId),
    // Must be non-empty: CourseTemplate.create throws on a blank title, which
    // would make loadInheritance reject and the editor's inherited preview null.
    // The title is irrelevant here — the composer sources inherited fields from
    // the Course/Program/Department, not the stub template.
    title: 'inheritance-preview',
    ...(courseId ? { courseId } : {}),
    ...(programId ? { programId } : {}),
    createdBy: 'preview',
  });
  return loadComposedSyllabus({ tenantId, template: stub });
}

/**
 * Reconstitute a CourseTemplate domain entity from a Firestore document.
 * Used when callers only have a templateId (e.g. portal flows where the
 * template is loaded server-side and shipped as DTO).
 */
export async function loadTemplate(
  tenantId: string,
  templateId: string,
): Promise<CourseTemplate | null> {
  const snap = await getDoc(doc(db, `tenants/${tenantId}/courseTemplates/${templateId}`));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return CourseTemplate.reconstitute({
    id: templateId,
    tenantId: TenantId.of(tenantId),
    title: (data['title'] as string) ?? '',
    code: (data['code'] as string) ?? '',
    version: (data['version'] as number) ?? 1,
    status: ((data['status'] as any) ?? 'draft') as any,
    description: (data['description'] as any) ?? {},
    objectives: (data['objectives'] as any) ?? { program: [], department: [], course: [] },
    philosophy: (data['philosophy'] as any) ?? [],
    grading: (data['grading'] as any) ?? {
      components: [],
      passingGrade: 4,
      expectedHours: [],
    },
    readings: (data['readings'] as any) ?? { required: [], complementary: { items: [] } },
    sessionFormat: (data['sessionFormat'] as any) ?? { weekdayTemplates: [] },
    modules: (data['modules'] as any) ?? [],
    assignments: (data['assignments'] as any) ?? [],
    ...(data['institutionalFramework']
      ? { institutionalFramework: data['institutionalFramework'] as any }
      : {}),
    ...(data['attendancePolicy'] ? { attendancePolicy: data['attendancePolicy'] as any } : {}),
    ...(data['conductRules'] ? { conductRules: data['conductRules'] as any } : {}),
    ...(data['latePolicy'] ? { latePolicy: data['latePolicy'] as any } : {}),
    ...(data['anchorVerse'] ? { anchorVerse: data['anchorVerse'] as any } : {}),
    ...(data['biblicalVersion'] ? { biblicalVersion: data['biblicalVersion'] as string } : {}),
    ...(data['source'] ? { source: data['source'] as any } : {}),
    ...(data['courseId'] ? { courseId: data['courseId'] as string } : {}),
    ...(data['programId'] ? { programId: data['programId'] as string } : {}),
    createdBy: (data['createdBy'] as string) ?? 'unknown',
    createdAt: toDate(data['createdAt']),
    updatedAt: toDate(data['updatedAt']),
  });
}

export type { ComposedSyllabus };
